// ===============================
// PERFORMANCE DRAWER TYPES
// ===============================

export const BLOOM_CONFIG = {
  Retention: { label: 'Retention', color: '#3b82f6', bg: '#dbeafe' },
  Comprehension: { label: 'Comprehension', color: '#6366f1', bg: '#e0e7ff' },
  Application: { label: 'Application', color: '#22c55e', bg: '#dcfce7' },
  Analysis: { label: 'Analysis', color: '#f59e0b', bg: '#fef3c7' },
  Evaluation: { label: 'Evaluation', color: '#ec4899', bg: '#fce7f3' },
  Creation: { label: 'Creation', color: '#a855f7', bg: '#f3e8ff' },
} as const;

export type BloomLevel = keyof typeof BLOOM_CONFIG;
export interface MasteryScore {
  value: number; // 0–100
  change: number; // session delta
}

export interface QuestionStats {
  total?: number;
  correct?: number;
  incorrect?: number;
  skipped?: number;
  accuracy?: number;
  attempted?: number;
}

export interface ConceptProgress {
  name: string;
  icon: string;
  correct: number;
  total: number;
  status: string;
}
export interface BloomStats {
  level:
    | 'Retention'
    | 'Analysis'
    | 'Application'
    | 'Comprehension'
    | 'Creation'
    | 'Evaluation';
  correct: number;
  total: number;
}

export interface DifficultyStats {
  level: 'easy' | 'medium' | 'hard';
  correct: number;
  total: number;
  timeTaken?: number;
}

export interface StrengthWeaknessItem {
  name: string;
  icon: string;
  accuracy: number;
  correct: number;
  incorrect: number;
  description?: string;
  bloomLevel?: keyof typeof BLOOM_CONFIG;
  bloomLevels?: (keyof typeof BLOOM_CONFIG)[];
}

export interface SessionHistoryItem {
  id: string;
  name: string;
  type: 'Warm-up' | 'Practice' | 'Challenge';
  questions: number;
  score?: number;
  isCurrent?: boolean;
  timeMeta?: string;
}
export type SessionMode = 'warmup' | 'learning' | 'practice' | 'challenge';

export interface SessionStats {
  mode: SessionMode;
  elapsed: string;
  questionStats: QuestionStats;
  masteryGain: number;
}

// ===============================
// OVERALL TAB DATA
// ===============================
export interface ConceptsData {
  noOfAttempted?: number;
  noOfMastered?: number;
  list: ConceptProgress[];
}
export interface OverallPerformanceData {
  mastery: MasteryScore;

  questions: QuestionStats;

  concepts: ConceptsData;

  bloom: BloomStats[];

  difficulty: DifficultyStats[];

  strengths: StrengthWeaknessItem[];
  weaknesses: StrengthWeaknessItem[];

  sessionHistory: SessionHistoryItem[];
}

// ===============================
// THIS SESSION TAB DATA
// ===============================

export interface ThisSessionPerformanceData {
  session: SessionStats;
  concepts: ConceptsData;
  bloom: BloomStats[];
  strengths: StrengthWeaknessItem[];
  weaknesses: StrengthWeaknessItem[];
  masteryGain?: number;
}

// ===============================
// FULL DRAWER DATA MODEL
// ===============================

export interface PerformanceDrawerData {
  overall: OverallPerformanceData;
  session: ThisSessionPerformanceData;
}

export const performanceDrawerDummyData: PerformanceDrawerData = {
  overall: {
    mastery: {
      value: 78,
      change: 0,
    },

    questions: {
      total: 23,
      correct: 15,
      incorrect: 6,
      skipped: 2,
    },

    concepts: {
      noOfAttempted: 3,
      noOfMastered: 1,
      list: [
        {
          name: 'Motion & Friction',
          icon: '🏃',
          correct: 9,
          total: 11,
          status: 'mastered',
        },
        {
          name: "Newton's Laws",
          icon: '⚖️',
          correct: 5,
          total: 9,
          status: 'attempted',
        },
        {
          name: 'Fluid Dynamics',
          icon: '🌊',
          correct: 1,
          total: 3,
          status: 'attempted',
        },
      ],
    },

    bloom: [
      { level: 'Retention', correct: 5, total: 5 },
      { level: 'Comprehension', correct: 3, total: 5 },
      { level: 'Application', correct: 4, total: 8 },
      { level: 'Analysis', correct: 3, total: 5 },
    ],

    difficulty: [
      { level: 'easy', correct: 7, total: 8 },
      { level: 'medium', correct: 6, total: 11 },
      { level: 'hard', correct: 2, total: 4 },
    ],

    strengths: [
      {
        name: 'Motion & Friction',
        icon: '🏃',
        accuracy: 82,
        correct: 9,
        incorrect: 2,
      },
    ],

    weaknesses: [
      {
        name: 'Fluid Dynamics',
        icon: '🌊',
        accuracy: 33,
        correct: 1,
        incorrect: 2,
      },
      {
        name: "Newton's Laws",
        icon: '⚖️',
        accuracy: 56,
        correct: 5,
        incorrect: 4,
      },
    ],

    sessionHistory: [
      {
        id: 's1',
        name: 'Current Session',
        type: 'Warm-up',
        questions: 0,
        isCurrent: true,
        timeMeta: '0:03',
        score: 0,
      },
      {
        id: 's2',
        name: '2026-04-07',
        type: 'Challenge',
        questions: 10,
        score: 70,
      },
      {
        id: 's3',
        name: '2026-04-05',
        type: 'Warm-up',
        questions: 5,
        score: 60,
      },
      {
        id: 's4',
        name: '2026-04-02',
        type: 'Practice',
        questions: 8,
        score: 63,
      },
    ],
  },

  session: {
    session: {
      mode: 'warmup',
      elapsed: '0:03',
      masteryGain: 0,
      questionStats: {
        correct: 0,
        incorrect: 1,
        skipped: 0,
        attempted: 0,
      },
    },
    masteryGain: -1,
    concepts: {
      list: [
        {
          name: 'Motion & Friction',
          icon: '🏃',
          correct: 0,
          total: 1,
          status: 'not-started',
        },
      ],
    },
    bloom: [
      {
        level: 'Application',
        correct: 0,
        total: 1,
      },
    ],
    strengths: [],
    weaknesses: [
      {
        name: 'Motion & Friction',
        icon: '🏃',
        accuracy: 0,
        correct: 0,
        incorrect: 1,
        description: '0% — 1 incorrect of 1. Review the learning resources.',
      },
    ],
  },
};
